/**
 * Format a resolved Real-Time Mode match into a markdown block suitable
 * for prepending to a chat message.
 *
 * Privacy: only the fields the user already sees in the sidebar are
 * included. Match basis is summarized — raw observed-context fields are
 * NOT echoed (they're already covered by `data.patient`).
 *
 * Shape of the block (markdown):
 *
 *   > **ModMed context — Patient Name**
 *   > DOB: MM/DD/YYYY · MRN: 12345
 *   > Match: High confidence (95%) — MRN + DOB
 *   > Outstanding balance: $143.22
 *   > Recent claims: 2025-09-12 Aetna (paid) …
 *   > Open issues: 1 medium — Patient has outstanding balance.
 *
 * The block is intentionally compact: the AI uses it for grounding, the
 * user sees a token-friendly summary, and no claim-level PHI leaks beyond
 * what the EMR already showed them.
 */

import type { MatchState, RealTimeModeData } from '@ctrl-enter/types';
import { providerDisplayName } from '@ctrl-enter/types';

const SEVERITY_ORDER: Record<'high' | 'medium' | 'low', number> = {
  high: 0,
  medium: 1,
  low: 2,
};

export function isAttachableMatchState(match: MatchState): boolean {
  return match.status === 'matched' || match.status === 'likely';
}

export function formatModmedContext(match: MatchState): string | null {
  if (match.status !== 'matched' && match.status !== 'likely') return null;
  const { data, confidence, basis } = match;

  const lines: string[] = [];
  lines.push(`**${providerDisplayName(match.context.provider)} context — ${data.patient.name}**`);

  const ident: string[] = [];
  if (data.patient.dob) ident.push(`DOB: ${formatDob(data.patient.dob)}`);
  if (data.patient.mrn) ident.push(`MRN: ${data.patient.mrn}`);
  if (ident.length > 0) lines.push(ident.join(' · '));

  const confidenceLabel =
    match.status === 'matched' ? 'High confidence' : 'Likely match';
  const basisLabel = basis
    .map(b => {
      switch (b) {
        case 'mrn':
          return 'MRN';
        case 'emr_account_id':
          return 'Account ID';
        case 'emr_patient_id':
          return 'EMR Patient ID';
        case 'dob':
          return 'DOB';
        case 'name':
          return 'Name';
      }
    })
    .join(' + ');
  lines.push(
    `Match: ${confidenceLabel} (${(confidence * 100).toFixed(0)}%)${
      basisLabel ? ' — ' + basisLabel : ''
    }`
  );

  lines.push(...formatFinancialLines(data));
  lines.push(...formatClaimsLines(data));
  lines.push(...formatIssuesLines(data));

  return lines.map(line => `> ${line}`).join('\n');
}

function formatFinancialLines(data: RealTimeModeData): string[] {
  const lines: string[] = [];
  const balance = data.financialSummary.outstandingPatientBalance;
  if (balance !== null) {
    lines.push(`Outstanding patient balance: ${formatCurrency(balance)}`);
  }
  const insBalance = data.financialSummary.outstandingInsuranceBalance;
  if (insBalance !== null && insBalance !== undefined && insBalance > 0) {
    lines.push(`Outstanding insurance balance: ${formatCurrency(insBalance)}`);
  }
  return lines;
}

function formatClaimsLines(data: RealTimeModeData): string[] {
  if (data.recentClaims.length === 0) return [];
  const top = data.recentClaims.slice(0, 5);
  const formatted = top
    .map(c => {
      // Both `dateOfService` and `payer` are optional upstream — render
      // a compact "(status)" when neither is present so the markdown
      // blockquote stays readable instead of showing "undefined".
      const parts: string[] = [];
      if (c.dateOfService) parts.push(formatDob(c.dateOfService));
      if (c.payer) parts.push(c.payer);
      parts.push(`(${c.status})`);
      return parts.join(' ');
    })
    .join('; ');
  return [`Recent claims (${top.length}): ${formatted}`];
}

function formatIssuesLines(data: RealTimeModeData): string[] {
  if (data.currentIssues.length === 0) return [];
  const sorted = [...data.currentIssues].sort(
    (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]
  );
  const top = sorted.slice(0, 3);
  return [
    `Open issues (${sorted.length}): ${top
      .map(i => `${i.severity} — ${i.message}`)
      .join('; ')}`,
  ];
}

function formatCurrency(value: number): string {
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDob(iso: string): string {
  const [year, month, day] = iso.split('-');
  if (!year || !month || !day) return iso;
  return `${month}/${day}/${year}`;
}
